<i18n src="./resources/crm/history-locales.json"></i18n>
<i18n src="../../mixins/ecs-time-formatter/resources/locales.json"></i18n>

<template>
  <div>
    <ecs-search-input
      v-if="expandedMode"
      class="flex pa-2"
      :placeholder="$t('searchPlaceholder')"
      @search-input-changed="handleSearch"
    />

    <v-data-table
      dense
      class="data-table surface lighten-1 custom-scrollbar"
      :headers="headers"
      :items="itemsToShow"
      :search="expandedMode ? expandedSearchText : searchText"
      :items-per-page="5"
      :items-per-page-text="$t('rowsPerPageText')"
      :item-class="checkCurrentItem"
      @dblclick:row="showConversationSmsHistory"
    >
      <template slot="items" slot-scope="props">
        <tr>
          <td v-for="(row, index) in rows" :key="index" class="text-right">
            <span> {{ props.item[row.value] }} </span>
          </td>
        </tr>
      </template>

      <v-alert v-once slot="no-results" color="error" icon="warning">
        {{ $t('noResultsFound') }}
      </v-alert>

      <template slot="pageText" slot-scope="props">
        {{ props.pageStart }} - {{ props.pageStop }} {{ $t('of') }}
        {{ props.itemsLength }}
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { MAX_NUMBER_MESSAGES } from '@/constants/smsConstants';
import EcsTimeFormatter from '@/mixins/ecs-time-formatter/EcsTimeFormatter';

export default {
  name: 'InteractionCrmHistoryTable',

  mixins: [EcsTimeFormatter],

  props: {
    items: {
      type: Array,
      default() {
        return [];
      }
    },
    expandedMode: {
      type: Boolean,
      default: false
    },
    searchText: {
      type: String,
      default: ''
    },
    verticalScroll: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      expandedSearchText: ''
    };
  },

  computed: {
    ...mapGetters('INTERACTIONS', ['getInteraction']),

    ...mapGetters('ELEMENTS', ['getElementIdsByInteractionId']),

    ...mapGetters('SETTINGS', ['applicationSettings']),

    itemsToShow() {
      return structuredClone(this.items).map(item => {
        Object.keys(item).forEach(key => {
          if (['startTime', 'endTime'].includes(key)) {
            item[key] = this.formattedDateTime(item[key]);
          }
        });
        return item;
      });
    },

    headers() {
      let headersToshow;
      if (this.expandedMode) {
        headersToshow = [
          { text: this.$t('startTime'), value: 'startTime' },
          { text: this.$t('endTime'), value: 'endTime' },
          { text: this.$t('lastAgent'), value: 'lastAgent' },
          { text: this.$t('interactionType'), value: 'interactionType' },
          { text: this.$t('status'), value: 'status' },
          {
            text: this.$t('lastBusinessProcess'),
            value: 'lastBusinessProcess'
          },
          { text: this.$t('origin'), value: 'origin' },
          { text: this.$t('destination'), value: 'destination' },
          { text: this.$t('agentTalkTime'), value: 'talkTime' },
          { text: this.$t('disposition'), value: 'disposition1' }
        ];
      } else {
        headersToshow = [
          { text: this.$t('startTime'), value: 'startTime' },
          { text: this.$t('lastAgent'), value: 'lastAgent' },
          { text: this.$t('interactionType'), value: 'interactionType' },
          { text: this.$t('status'), value: 'status' },
          { text: this.$t('lastBusinessProcess'), value: 'lastBusinessProcess' }
        ];
      }
      return headersToshow;
    },

    rows() {
      let rowsToShow;
      if (this.expandedMode) {
        rowsToShow = [
          { value: 'startTime' },
          { value: 'endTime' },
          { value: 'lastAgent' },
          { value: 'interactionType' },
          { value: 'status' },
          { value: 'lastBusinessProcess' },
          { value: 'origin' },
          { value: 'destination' },
          { value: 'agentTalkTime' },
          { value: 'disposition' }
        ];
      } else {
        rowsToShow = [
          { value: 'startTime' },
          { value: 'lastAgent' },
          { value: 'interactionType' },
          { value: 'status' },
          { value: 'lastBusinessProcess' }
        ];
      }
      return rowsToShow;
    }
  },

  methods: {
    ...mapActions('CONVERSATIONS', ['loadInteractionSmsHistory']),

    ...mapActions('INTERACTIONS', ['addAgentInteractionHistory']),

    ...mapActions('WINDOWS', ['restoreInteractionWindow', 'setFocusedInteractionId']),

    handleSearch(searchText) {
      this.expandedSearchText = searchText;
    },

    showConversationSmsHistory(event, item) {
      if (this.isSms(item.item)) {
        this.addAgentInteractionHistory(item.item);
        this.loadInteractionSmsHistory({
          interactionId: item.item.interactionId,
          elementId: this.getElementIdsByInteractionId(item.item.interactionId)[0],
          maxNumber: MAX_NUMBER_MESSAGES,
          fullHistory: true
        }).catch(error => {
          console.log(error);
        });
        this.restoreInteractionWindow(item.item.interactionId);
        this.setFocusedInteractionId(item.item.interactionId);
      }
    },

    checkCurrentItem(item) {
      return this.isSms(item) ? 'selectable-row' : '';
    },

    isSms(item) {
      return item.interactionType.includes('SMS');
    }
  }
};
</script>

<style scoped>
.v-data-table-header {
  white-space: nowrap;
}

.v-data-table__overflow {
  width: 100%;
  overflow-x: unset;
  overflow-y: unset;
}

.data-table {
  border-radius: 0 0 8px 8px;
}

table.v-data-table thead tr th,
table.v-data-table tbody td {
  padding: 0 10px !important;
}

table.v-data-table tbody td {
  text-align: left !important;
}

.data-table .v-data-table__actions {
  flex-direction: row;
  width: 370px;
}

.data-table .v-data-table__actions__select {
  max-width: 160px;
}
.v-data-table__actions__select .v-select {
  margin: 0 0 0 15px;
}

.v-data-table__actions__pagination {
  margin: 0;
}

.v-data-table__actions__select .v-text-field .v-input__prepend-inner,
.v-text-field .v-input__append-inner {
  margin-top: 0px;
}
</style>
<style>
.selectable-row {
  cursor: pointer;
  user-select: none;
}
</style>
